import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { track as trackAnalytics } from './utils/analyticsTracker';
import TrackingScripts from './components/TrackingScripts';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { initializeSecurityHardening } from './utils/securityHardening';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';

import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminRecovery from './pages/AdminRecovery';
import Home from './pages/Home';
import Profile from './pages/Profile';
import IntakeRequired from './pages/IntakeRequired';
import AcceptInvite from './pages/AcceptInvite';
import GuestBooking from './pages/GuestBooking';
import GroomerSchedules from './pages/GroomerSchedules';
import BookAppointment from './pages/BookAppointment';
import AppointmentsEnhanced from './pages/AppointmentsEnhanced';
import AdminDashboard from './pages/AdminDashboard';
import AvailabilityCalendar from './pages/AvailabilityCalendar';
import TimeOffManagement from './pages/TimeOffManagement';
import ClientManagement from './pages/ClientManagement';
import UserManagement from './pages/UserManagement';
import MessagingCenter from './pages/MessagingCenter';
import QuickBuy from './pages/QuickBuy';
import ServicesManagement from './pages/ServicesManagement';
import Recommendations from './pages/Recommendations';
import RecommendationsManager from './pages/RecommendationsManager';
import Stats from './pages/Stats';
import InventoryManagement from './pages/InventoryManagement';
import FormFieldsManager from './pages/FormFieldsManager';
import InvoiceTemplatesManager from './pages/InvoiceTemplatesManager';
import InvoiceTemplateEditor from './pages/InvoiceTemplateEditor';
import FormsManager from './pages/FormsManager';
import FormEditor from './pages/FormEditor';
import FormFillPage from './pages/FormFillPage';
import FormSubmissions from './pages/FormSubmissions';
import PetChart from './pages/PetChart';
import SubjectManager from './pages/SubjectManager';
import BookingFormLabelsManager from './pages/BookingFormLabelsManager';
import SetupWizard from './pages/SetupWizard';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import InstallmentPaymentPage from './pages/InstallmentPaymentPage';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * AnalyticsTracker — fires a page_view ping on every SPA navigation. Mounted
 * inside Router so useLocation works. Renders nothing.
 *
 * Excludes the public installment payment page (/pay-installment/:token)
 * from tracking so token strings never end up in the page_views table.
 */
const AnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/pay-installment/')) return;
    trackAnalytics({ path: location.pathname });
  }, [location.pathname]);
  return null;
};

function App() {
  const [setupStatus, setSetupStatus] = useState({ loading: true, setupComplete: null });

  useEffect(() => {
    // Initialize security hardening on app load
    initializeSecurityHardening('Dog Grooming System');
    
    // Check if setup is complete
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    // Skip setup check in demo mode — always show the normal app
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('demo') === '1') {
      setSetupStatus({ loading: false, setupComplete: true });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/setup/status`);
      const data = await response.json();
      setSetupStatus({ loading: false, setupComplete: data.setupComplete });
    } catch (error) {
      console.error('Failed to check setup status:', error);
      // If we can't reach the server, assume setup is complete (existing installation)
      setSetupStatus({ loading: false, setupComplete: true });
    }
  };

  // Show loading while checking setup status
  if (setupStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show setup wizard if setup is not complete
  if (!setupStatus.setupComplete) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<SetupWizard />} />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </Router>
    );
  }

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <AnalyticsTracker />
        <TrackingScripts />
        <div className="App">
          <Navbar />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin-recovery" element={<AdminRecovery />} />

            {/* Public, token-authenticated installment payment page. The
                token in the URL is the only credential — no login required. */}
            <Route path="/pay-installment/:token" element={<InstallmentPaymentPage />} />

            {/* Public employee invitation accept page (roadmap item #17).
                Token-gated; renders without an existing session. On
                success it auto-logs the new hire in via
                AuthContext.applySession. */}
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />

            {/* Public guest booking page (roadmap item #20). Renders
                without auth; /api/guest-booking/config is also public
                so the page can verify the feature is enabled before
                showing the form. */}
            <Route path="/book/guest" element={<GuestBooking />} />
            
            {/* Intake gate: shown to new self-signed-up clients with
                outstanding REQUIRED intake document requests, when the
                admin has block_signup_until_required_documents enabled.
                allowWhileIntakeGated lets ProtectedRoute render this
                page even though the gate flag is on. */}
            <Route
              path="/intake-required"
              element={
                <ProtectedRoute allowWhileIntakeGated>
                  <IntakeRequired />
                </ProtectedRoute>
              }
            />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/book"
              element={
                <ProtectedRoute>
                  <BookAppointment />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/appointments"
              element={
                <ProtectedRoute>
                  <AppointmentsEnhanced />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/availability"
              element={
                <ProtectedRoute>
                  <AvailabilityCalendar />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/time-off"
              element={
                <ProtectedRoute>
                  <TimeOffManagement />
                </ProtectedRoute>
              }
            />

            {/* Roadmap item #17 Phase 2: groomer-facing self-edit page
                for working hours. The page itself enforces the
                employees_can_set_own_hours setting + falls back to
                read-only when disabled, so we only need a generic
                ProtectedRoute here. */}
            <Route
              path="/my-hours"
              element={
                <ProtectedRoute>
                  <GroomerSchedules selfMode />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <ClientManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/messaging"
              element={
                <ProtectedRoute requireAdmin>
                  <MessagingCenter />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/quick-buy"
              element={
                <ProtectedRoute requireAdmin>
                  <QuickBuy />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/services-management"
              element={
                <ProtectedRoute requireAdmin>
                  <ServicesManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/form-fields"
              element={
                <ProtectedRoute requireAdmin>
                  <FormFieldsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoice-templates"
              element={
                <ProtectedRoute requireAdmin>
                  <InvoiceTemplatesManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoice-templates/:id"
              element={
                <ProtectedRoute requireAdmin>
                  <InvoiceTemplateEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forms"
              element={
                <ProtectedRoute requireAdmin>
                  <FormsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forms/:id"
              element={
                <ProtectedRoute requireAdmin>
                  <FormEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forms/:id/submissions"
              element={
                <ProtectedRoute requireAdmin>
                  <FormSubmissions />
                </ProtectedRoute>
              }
            />
            {/* Public form fill — token-gated, no login required */}
            <Route path="/forms/fill/:token" element={<FormFillPage />} />

            {/* Per-subject chart — admin or groomer (backend enforces RBAC) */}
            <Route
              path="/pets/:id/chart"
              element={
                <ProtectedRoute>
                  <PetChart />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/booking-form-labels"
              element={
                <ProtectedRoute requireAdmin>
                  <BookingFormLabelsManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/subject-manager"
              element={
                <ProtectedRoute requireAdmin>
                  <SubjectManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/recommendations"
              element={
                <ProtectedRoute>
                  <Recommendations />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/recommendations-manager"
              element={
                <ProtectedRoute requireAdmin>
                  <RecommendationsManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/stats"
              element={
                <ProtectedRoute requireAdmin>
                  <Stats />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/inventory"
              element={
                <ProtectedRoute requireAdmin>
                  <InventoryManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/about"
              element={
                <ProtectedRoute>
                  <AboutUs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/contact"
              element={
                <ProtectedRoute>
                  <ContactUs />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
